import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { v2Api, type ScenarioInject } from '@/lib/api';
import { stripHtml } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Settings2 } from 'lucide-react';

interface Props {
  scenarioId: string;
}

export function ScenarioInjectsTab({ scenarioId }: Props) {
  const [injects, setInjects] = useState<ScenarioInject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await v2Api.dashboard.scenarios.injects.list(scenarioId);
      setInjects(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load injects');
      setInjects([]);
    } finally {
      setLoading(false);
    }
  }, [scenarioId]);

  useEffect(() => {
    fetchInjects();
  }, [fetchInjects]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-center text-sm text-destructive">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {injects.length} inject{injects.length !== 1 ? 's' : ''} for this scenario
        </p>
        <Button asChild size="sm" variant="outline">
          <Link to={`/injects?scenario=${scenarioId}`}>
            <Settings2 className="mr-2 h-4 w-4" />
            Manage injects
          </Link>
        </Button>
      </div>
      {injects.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          No injects yet. Click &quot;Manage injects&quot; to add and edit injects for this scenario.
        </div>
      ) : (
        <div className="space-y-3">
          {injects
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((inject) => (
              <Card key={inject.id}>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-muted-foreground font-normal">#{inject.order}</span>
                    {inject.title}
                  </CardTitle>
                  <CardDescription className="mt-1 line-clamp-2">{stripHtml(inject.description)}</CardDescription>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-xs px-2 py-0.5 rounded bg-muted capitalize">{inject.severity}</span>
                    {inject.inject_type?.name && (
                      <span className="text-xs px-2 py-0.5 rounded bg-muted">{inject.inject_type.name}</span>
                    )}
                    {inject.points > 0 && (
                      <span className="text-xs text-muted-foreground">{inject.points} pts</span>
                    )}
                    {inject.scheduled_offset_minutes > 0 && (
                      <span className="text-xs text-muted-foreground">
                        +{inject.scheduled_offset_minutes} min
                      </span>
                    )}
                    {Array.isArray(inject.ttx_config_ids) && inject.ttx_config_ids.length > 1 && (
                      <span className="text-xs text-muted-foreground">
                        Linked to {inject.ttx_config_ids.length} scenarios
                      </span>
                    )}
                  </div>
                </CardHeader>
              </Card>
            ))}
        </div>
      )}
    </div>
  );
}
